import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { serverDomain } from "../../config";
import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import moment from "moment-timezone";
import "../../styles/PaymentsPage.css";

/* ---------- FL Modal (без react-modal) ---------- */
const FlModal = ({ open, title, onClose, children, footer, width = 420 }) => {
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onEsc = (e) => e.key === "Escape" && onClose();
    const onDown = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) onClose();
    };

    document.addEventListener("keydown", onEsc);
    document.addEventListener("mousedown", onDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onEsc);
      document.removeEventListener("mousedown", onDown);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="flm-overlay" role="dialog" aria-label={title}>
      <div className="flm" ref={boxRef} style={{ width: `min(${width}px, 96vw)` }}>
        <div className="flm__head">
          <div className="flm__title">{title}</div>
          <button className="flm__close" type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>

        <div className="flm__body">{children}</div>

        {footer ? <div className="flm__footer">{footer}</div> : null}
      </div>
    </div>
  );
};

const methodLabel = (m) => {
  switch (m) {
    case "USDT_TRC20": return "USDT TRC20";
    case "USDT_BEP20": return "USDT BEP20";
    case "IP_OOO": return "ИП/ООО";
    case "SELF_EMPLOYED": return "Самозанятый";
    case "CRYPTO_BOT_CHECK": return "Crypto Bot Чек";
    default: return "Неизвестно";
  }
};

const PaymentsPage = () => {
  const navigate = useNavigate();

  const [payouts, setPayouts] = useState([]);
  const [balance, setBalance] = useState(0);

  const [wallets, setWallets] = useState([]);
  const [newAmount, setNewAmount] = useState("0");
  const [newWallet, setNewWallet] = useState("");

  const [loading, setLoading] = useState(true);
  const [creationLoading, setCreationLoading] = useState(false);

  const [error, setError] = useState(null);
  const [creationError, setCreationError] = useState(null);

  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);

  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);

  const [payoutsThisMonth, setPayoutsThisMonth] = useState(0);
  const limitReached = payoutsThisMonth >= 2;
  const [noAllowedWallets, setNoAllowedWallets] = useState(false);

  const userTimezone = useMemo(() => moment.tz.guess(), []);
  const token = useMemo(() => localStorage.getItem("token"), []);

  const isWalletBlocked = (w) =>
    w?.type === "USDT_TRC20" || w?.type === "USDT_BEP20" || w?.type === "CRYPTO_BOT_CHECK";

  const firstAllowedWalletId = (arr) => {
    const ok = (arr || []).find((w) => !isWalletBlocked(w));
    return ok ? String(ok.id) : "";
  };

  const calcPayoutsThisMonth = (list) => {
    const now = moment().tz(userTimezone);
    const monthStart = now.clone().startOf("month");
    const monthEnd = now.clone().endOf("month");
  
    return (list || []).filter(p =>
      p?.created_at &&
      moment.utc(p.created_at).tz(userTimezone).isBetween(monthStart, monthEnd, null, "[]")
    ).length;
  };

  const formatDateParts = (isoDate) => {
    if (!isoDate) return { date: "—", time: "" };
  
    const m = moment.utc(isoDate).tz(userTimezone);
    return {
      date: m.format("DD.MM.YYYY"),
      time: m.format("HH:mm:ss"),
    };
  };

  const walletValue = (p) => {
    if (p.payment_method === "USDT_TRC20" || p.payment_method === "USDT_BEP20") return p.usdt_wallet || "—";
    if (p.payment_method === "IP_OOO") return p.org_name || "—";
    if (p.payment_method === "SELF_EMPLOYED") return p.contract_number || "—";
    if (p.payment_method === "CRYPTO_BOT_CHECK") return p.telegram_user || "—";
    return "—";
  };

  const fetchAll = async () => {
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // payouts
      const payoutsRes = await fetch(`${serverDomain}/api/payments/payouts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!payoutsRes.ok) throw new Error("Ошибка при получении выплат");
      const payoutsData = await payoutsRes.json();
      setPayouts(payoutsData || []);

      // count this month
      const now = moment().tz(userTimezone);
      const monthStart = now.clone().startOf("month");
      const monthEnd = now.clone().endOf("month");
      const cnt = calcPayoutsThisMonth(payoutsData);
      setPayoutsThisMonth(cnt);

      // balance
      const balRes = await fetch(`${serverDomain}/api/settings/user_balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const balData = await balRes.json().catch(() => ({}));
      setBalance(parseFloat(balData.balance || 0));

      // wallets
      const walletsRes = await fetch(`${serverDomain}/api/payments/wallets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const walletsData = await walletsRes.json().catch(() => []);
      setWallets(walletsData || []);
      const allowedId = firstAllowedWalletId(walletsData);
      setNewWallet(allowedId);
      setNoAllowedWallets(!allowedId);

    } catch (e) {
      console.error(e);
      setError("Ошибка загрузки выплат");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleSendVerificationCode = async () => {
    if (!token) return;

    setVerificationLoading(true);
    setVerificationError(null);
    try {
      const res = await fetch(`${serverDomain}/api/payments/sendVerificationCode`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Не удалось отправить код");
    } catch (e) {
      setVerificationError(e.message);
    } finally {
      setVerificationLoading(false);
    }
  };

  const openVerifyModal = async () => {
    setCreationError(null);
    setVerificationError(null);
    setVerificationCode("");

    // лимит
    if (payoutsThisMonth >= 2) {
      setShowLimitModal(true);
      return;
    }

    // нет доступных кошельков
    if (noAllowedWallets) {
      setCreationError("Сейчас доступен только крипто-кошелёк. Вывод по нему временно недоступен. Добавьте другой способ вывода.");
      return;
    }

    // выбран заблокированный
    const selected = wallets.find((w) => String(w.id) === String(newWallet));
    if (!selected || isWalletBlocked(selected)) {
      const allowedId = firstAllowedWalletId(wallets);
      setNewWallet(allowedId);
      setCreationError("Выбранный метод вывода временно недоступен. Выберите другой способ вывода.");
      return;
    }

    // проверка суммы
    const amount = parseFloat(newAmount);
    if (isNaN(amount) || amount <= 0) {
      setCreationError("Введите корректную сумму");
      return;
    }

    setShowVerifyModal(true);
    await handleSendVerificationCode();
  };

  const closeVerifyModal = () => {
    setShowVerifyModal(false);
    setVerificationCode("");
    setVerificationError(null);
  };

  const createPayout = async () => {
    setCreationError(null);

    const selected = wallets.find((w) => String(w.id) === String(newWallet));
    if (!selected || isWalletBlocked(selected)) {
      setCreationError("Выбранный метод вывода временно недоступен. Выберите другой способ вывода.");
      return;
    }

    if (!token) {
      navigate("/login");
      return;
    }

    let amount = parseFloat(newAmount);
    if (isNaN(amount) || amount <= 0) {
      setCreationError("Введите корректную сумму");
      return;
    }
    if (amount > balance) amount = balance;

    if (!verificationCode || verificationCode.trim().length === 0) {
      setVerificationError("Введите код");
      return;
    }

    setCreationLoading(true);
    try {
      const res = await fetch(`${serverDomain}/api/payments/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          wallet_id: newWallet,
          amount,
          verificationCodeInput: verificationCode.trim(),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Ошибка при создании выплаты");

      // обновляем список
      const payoutsRes = await fetch(`${serverDomain}/api/payments/payouts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updated = await payoutsRes.json().catch(() => []);

      setPayouts(updated);
      setPayoutsThisMonth(calcPayoutsThisMonth(updated));
      setBalance((prev) => prev - amount);
      setNewAmount("0");
      closeVerifyModal();
    } catch (e) {
      setCreationError(e.message);
    } finally {
      setCreationLoading(false);
    }
  };

  const disableOrder =
    creationLoading ||
    !newWallet ||
    isNaN(parseFloat(newAmount)) ||
    parseFloat(newAmount) <= 0;

  return (
    <div>
      <TopBar pageTitle={"Выплаты"} />

      <div className="main-container">
        <SideBar />

        <main className="home-main flex-grow-1">
          <div className="fl-page fl-payments">
            {/* Заказ выплаты */}
            <section className="fl-cardbox">
              <div className="fl-cardbox__head">
                <h2 className="fl-h2">Заказ выплаты</h2>
                <button className="fl-btn fl-btn--ghost" type="button" onClick={fetchAll} disabled={loading}>
                  Обновить
                </button>
              </div>

              <div className="fl-formRow">
                <div className="fl-field">
                  <div className="fl-label">Счёт</div>
                  <input className="fl-input" value={balance.toFixed(2)} readOnly />
                </div>

                <div className="fl-field">
                  <div className="fl-label">Сумма</div>
                  <input
                    className="fl-input"
                    type="number"
                    step="0.01"
                    value={newAmount}
                    onChange={(e) => {
                      const v = e.target.value.trim();
                      const n = parseFloat(v);
                      if (v === "") { setNewAmount(""); return; }
                      if (isNaN(n)) { setNewAmount(""); return; }
                      if (n > balance) { setNewAmount(String(balance)); return; }
                      setNewAmount(v);
                    }}
                  />
                </div>

                <div className="fl-field fl-field--grow">
                  <div className="fl-label">Метод оплаты</div>
                  <select
                    className="fl-select"
                    value={newWallet}
                    onChange={(e) => setNewWallet(e.target.value)}
                  >
                    {wallets.map((w) => {
                      const disabled = isWalletBlocked(w);
                      const label = methodLabel(w.type);

                      const value =
                        w.usdt_wallet ||
                        w.contract_number ||
                        w.org_name ||
                        w.account_number ||
                        w.telegram_user ||
                        "";

                      return (
                        <option
                          key={w.id}
                          value={w.id}
                          disabled={disabled}
                        >
                          {label} — {value}{disabled ? " (временно недоступно)" : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="fl-field fl-field--btn">
                  <div className="fl-label">&nbsp;</div>
                  <button
                    className="fl-btn"
                    type="button"
                    onClick={openVerifyModal}
                    disabled={disableOrder  || limitReached}
                  >
                    {creationLoading ? "Обработка..." : "Заказать выплату"}
                  </button>
                </div>
              </div>

              <div className="fl-note">
                Общие выплаты работников можно посмотреть здесь:{" "}
                <a
                  className="fl-link"
                  href="https://t.me/+QeSkzeHIMt85Mzdi"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://t.me/+QeSkzeHIMt85Mzdi
                </a>
              </div>

              {payoutsThisMonth >= 2 ? (
                <div className="fl-alert fl-alert--error">
                  Раз в месяц можно заказать не более 2 выплат. В этом месяце уже создано: <b>{payoutsThisMonth}</b>
                </div>
              ) : null}
              
              {noAllowedWallets ? (
                    <div className="fl-alert fl-alert--warn" style={{ marginTop: 8 }}>
                      Сейчас доступен только крипто-кошелёк. Вывод по нему временно недоступен — добавьте другой способ вывода.
                    </div>
                  ) : null}

              {creationError ? <div className="fl-alert fl-alert--error">{creationError}</div> : null}
            </section>

            {/* Таблица выплат */}
            <section className="fl-cardbox">
              <div className="fl-cardbox__head">
                <h2 className="fl-h2">История выплат</h2>
              </div>

              {loading ? (
                <div className="loading-container"><div className="loader" /></div>
              ) : error ? (
                <div className="fl-alert fl-alert--error">{error}</div>
              ) : (
                <div className="fl-table-wrap">
                  <table className="fl-table fl-payments__table">
                    <thead>
                      <tr>
                        <th>Дата создания</th>
                        <th className="fl-hide-sm">Дата проведения</th>
                        <th>Сумма</th>
                        <th className="fl-hide-sm">Комиссия</th>
                        <th className="fl-hide-sm">Метод</th>
                        <th>Кошелёк</th>
                        <th className="fl-hide-sm">Комментарий</th>
                        <th>Статус</th>
                      </tr>
                    </thead>

                    <tbody>
                      {payouts.map((p) => {
                        const net = (Number(p.original_amount || 0) - Number(p.fee || 0)).toFixed(2);
                        const fee = Number(p.fee || 0).toFixed(2);

                        return (
                          <tr key={p.id}>
                            <td className="fl-col-date">
                              {(() => {
                                const d = formatDateParts(p.created_at);
                                return (
                                  <div className="fl-date2">
                                    <span className="fl-date2__date">{d.date}</span>
                                    <span className="fl-date2__time">{d.time}</span>
                                  </div>
                                );
                              })()}
                            </td>

                            <td className="fl-hide-sm fl-col-date">
                              {p.status === "pending" ? (
                                "—"
                              ) : (
                                (() => {
                                  const d = formatDateParts(p.updated_at);
                                  return (
                                    <div className="fl-date2">
                                      <span className="fl-date2__date">{d.date}</span>
                                      <span className="fl-date2__time">{d.time}</span>
                                    </div>
                                  );
                                })()
                              )}
                            </td>
                            <td>{net} ₽</td>
                            <td className="fl-hide-sm">{fee} ₽</td>
                            <td className="fl-hide-sm">{methodLabel(p.payment_method)}</td>
                            <td title={walletValue(p)} className="fl-col-wallet">
                              <div className="fl-walletCell">{walletValue(p)}</div>
                            </td>

                            <td className="fl-hide-sm fl-col-comment">
                              <div className="fl-commentCell">{p.comment || "—"}</div>
                            </td>
                            <td>
                              <span className={`fl-status fl-status--${p.status}`}>
                                {p.status === "pending" ? "Ожидает" : p.status === "approved" ? "Выплачено" : "Отклонено"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}

                      {payouts.length === 0 && (
                        <tr>
                          <td colSpan={8} style={{ textAlign: "center", padding: 16, opacity: 0.7 }}>
                            Выплат пока нет
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        </main>
      </div>

      {/* Модалка: код подтверждения */}
      <FlModal
        open={showVerifyModal}
        title="Подтверждение выплаты"
        onClose={closeVerifyModal}
        width={420}
        footer={
          <div className="flm__footerRow">
            <button className="fl-btn fl-btn--ghost" type="button" onClick={closeVerifyModal}>
              Закрыть
            </button>
            <button className="fl-btn" type="button" onClick={createPayout} disabled={creationLoading || !verificationCode}>
              {creationLoading ? "Отправка..." : "Подтвердить"}
            </button>
          </div>
        }
      >
        <div className="fl-field">
          <div className="fl-label">Введите код с почты</div>
          <input
            className="fl-input fl-input--wide"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="Код"
          />
        </div>

        {verificationError ? <div className="fl-alert fl-alert--error">{verificationError}</div> : null}
        {verificationLoading ? <div className="fl-mini">Отправляем код…</div> : null}

        <button
          className="fl-linklike"
          type="button"
          onClick={handleSendVerificationCode}
          disabled={verificationLoading}
          style={{ marginTop: 10 }}
        >
          Отправить код ещё раз
        </button>
      </FlModal>

      {/* Модалка: лимит */}
      <FlModal
        open={showLimitModal}
        title="Лимит выплат"
        onClose={() => setShowLimitModal(false)}
        width={420}
        footer={
          <div className="flm__footerRow">
            <button className="fl-btn" type="button" onClick={() => setShowLimitModal(false)}>
              Понятно
            </button>
          </div>
        }
      >
        <div className="fl-limitText">
          Раз в месяц можно заказать не более <b>2 выплат</b>, просьба соблюдать данное правило.
        </div>
        <div className="fl-limitText" style={{ marginTop: 10 }}>
          В этом месяце уже создано: <b>{payoutsThisMonth}</b>
        </div>
      </FlModal>
    </div>
  );
};

export default PaymentsPage;